// apps/admin/src/pages/billing/billing-shared.ts
//
// Tiny helpers shared across the four admin billing config pages
// (Fraud / Gateway / IMAP / FX). Phase-local — not promoted to
// @vollxssp/utils because the envelope shape is API-specific and
// the idempotency-key generator is just a thin wrapper around
// `crypto.randomUUID()`.
//
// Per CONTEXT.md.

/** Standard success envelope returned by the admin API surface. */
export interface SuccessEnvelope<T> {
  success: true;
  data: T;
  requestId: string;
}

/** Standard error envelope (Block 3 contract shape — `{ error: { code, message } }`). */
export interface ErrorEnvelope {
  success: false;
  error: { code: string; message: string };
  requestId: string;
}

/** Unwrap `{ success: true, data: T }` envelope; passthrough if already raw. */
export function unwrap<T>(payload: unknown): T {
  if (
    payload &&
    typeof payload === 'object' &&
    'success' in payload &&
    (payload as SuccessEnvelope<T>).success === true &&
    'data' in payload
  ) {
    return (payload as SuccessEnvelope<T>).data;
  }
  return payload as T;
}

/**
 * Pull a user-facing message out of an envelope error OR a fetch failure.
 * Returns null if no recognizable shape — caller falls back to "Network
 * error — check connection" (per VG executor rule §error-handling-rule-4).
 */
export function envelopeError(payload: unknown): string | null {
  if (
    payload &&
    typeof payload === 'object' &&
    'success' in payload &&
    (payload as ErrorEnvelope).success === false &&
    'error' in payload
  ) {
    return (payload as ErrorEnvelope).error?.message ?? null;
  }
  if (
    payload &&
    typeof payload === 'object' &&
    'error' in payload &&
    (payload as { error?: { message?: string } }).error?.message
  ) {
    return (payload as { error: { message: string } }).error.message;
  }
  return null;
}

/**
 * Generate an Idempotency-Key (per F-04) for mutation requests.
 * MUST be regenerated per logical operation — DO NOT cache across submits
 * (would cause server to short-circuit retries with the cached prior response).
 */
export function newIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Defensive fallback — older browsers without crypto.randomUUID.
  return `idemp-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

/**
 * Loosely-typed openapi-fetch client surface used by these pages. The
 * generated `paths` type may not include the admin billing-config routes
 * until the OpenAPI spec is regenerated post-Wave 8 — we cast through this
 * shape to keep the pages compiling without coupling to a not-yet-published
 * schema (same pattern as PreferencesPage).
 */
export interface LooseClient {
  GET: (
    path: string,
    init?: { headers?: Record<string, string>; params?: unknown },
  ) => Promise<{ data?: unknown; error?: unknown; response: Response }>;
  PUT: (
    path: string,
    init?: { headers?: Record<string, string>; body?: unknown; params?: unknown },
  ) => Promise<{ data?: unknown; error?: unknown; response: Response }>;
  POST: (
    path: string,
    init?: { headers?: Record<string, string>; body?: unknown; params?: unknown },
  ) => Promise<{ data?: unknown; error?: unknown; response: Response }>;
  DELETE: (
    path: string,
    init?: { headers?: Record<string, string>; params?: unknown },
  ) => Promise<{ data?: unknown; error?: unknown; response: Response }>;
  PATCH: (
    path: string,
    init?: { headers?: Record<string, string>; body?: unknown; params?: unknown },
  ) => Promise<{ data?: unknown; error?: unknown; response: Response }>;
}
